import React, {
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Row, Col } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import { GridReadyEvent, IFilterDef } from "ag-grid-community";
import { useHistory } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import { setSelectedInvoiceId } from "../../../redux/reducers/TagLevel";
import {
  getInvoicesTagLevels,
  getJobTransactions2,
  getJobTransactions2TagLevels,
  deleteTransactionLine,
} from "../../../docuclipper/api";
import { getCustomUi } from "../../Docuclipper/ConfirmAlert/ConfirmAlert";
import { ReduxState } from "../../../redux";
import ExternalFilter from "src/views/Docuclipper/ExternalFilter";
import {
  setExternalFiltersColumnDefs,
  clearExternalFilters,
} from "src/redux/reducers/ExternalFilter";
import { TableFilterConfig } from "src/docuclipper/DocuclipperTypes";
import { datefilterParamsYYYYMMDD } from "../../Pages/Analyze/AnalyzeTransactionsTable";
import { createAlert } from "src/redux/reducers/Alerts";
import { convertFilterModel, convertSortModel } from "src/utils/filters";
import AddDocumentsModal from "../DataArchive/TransactionManager/AddDocumentsModal";
import AddDocumentsPopover from "../DataArchive/TransactionManager/AddBankStatementsPdfCsvPopover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setDownloadInvoiceModalOpen } from "../../../redux/reducers/JobExport";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { DownloadInvoiceModal } from "../DataArchive/TransactionManager/DownloadInvoiceModal";
import { useRecentDocument } from "src/hooks/useRecentNavigation";

const getType = (documentType) => {
  if (["payables", "bill", "expense"].includes(documentType)) {
    return "payables";
  } else if (["receivables", "invoice"].includes(documentType)) {
    return "receivables";
  }
  throw new Error(`Unknown document type: ${documentType}`);
};

const formatData = (data) => {
  // Ensure data is an array before calling map
  if (!Array.isArray(data)) {
    console.warn("formatData received non-array data:", data);
    return [];
  }

  return data.map((item) => ({
    id: item.transactionLineId,
    invoiceId: item.invoiceId,
    invoiceDate: item.documentDate,
    invoiceVendor: item.vendorNormalized || "",
    invoiceCustomer: item.customerNormalized || "",
    invoiceAmount: parseFloat(item.amount) || 0,
    invoiceType: getType(item.documentType),
    docType: item.documentType,
    approvalStatus: item.approvalStatus || "",
    hasPOMatch: String(Boolean(item.hasPOMatch)),
    hasBankMatch: String(Boolean(item.hasBankMatch)),
  }));
};

export const Invoices = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const gridApi = useRef<any>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [hasData, setHasData] = useState<boolean | null>(null); // null = loading, true = has data, false = empty

  const { externalFilters } = useSelector(
    (state: ReduxState) => state.ExternalFilter
  );
  const job = useSelector((state: ReduxState) => state.JobData.job);

  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );

  // Track this page view as a recent document
  useRecentDocument(
    l1 && l2
      ? {
          docType: "invoices",
          label: `Invoices - ${l2}`,
          icon: "file-invoice",
          l1: l1,
          l2: l2,
        }
      : null
  );

  const types = [
    {
      label: "Receivables / Invoices",
      value: "receivables",
    },
    {
      label: "Payables / Bills / Expenses",
      value: "payables",
    },
  ];

  const docTypes = [
    {
      label: "Payables",
      value: "payables",
    },
    {
      label: "Bill",
      value: "bill",
    },
    {
      label: "Expense",
      value: "expense",
    },
    {
      label: "Receivables",
      value: "receivables",
    },
    {
      label: "Invoice",
      value: "invoice",
    },
  ];

  const externalFilterColumns = useMemo(
    () => [
      {
        field: "invoiceId",
        label: "Invoice ID",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
        configurable: true,
      },
      {
        field: "invoiceDate",
        label: "Date",
        filter: true,
        filterType: "date",
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "invoiceAmount",
        label: "Amount",
        filter: true,
        filterType: "number",
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "invoiceType",
        label: "Type",
        filter: true,
        filterType: "select",
        options: types,
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "invoiceVendor",
        label: "Vendor",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
        configurable: true,
      },
      {
        field: "invoiceCustomer",
        label: "Customer",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
        configurable: true,
      },
      {
        field: "approvalStatus",
        label: "Approval Status",
        filter: true,
        filterType: "select",
        options: [
          { label: "Pending", value: "pending" },
          { label: "Approved", value: "approved" },
        ],
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "hasPOMatch",
        label: "Has PO Match",
        filter: true,
        filterType: "select",
        options: [
          { label: "Yes", value: "true" },
          { label: "No", value: "false" },
        ],
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "hasBankMatch",
        label: "Has Bank Match",
        filter: true,
        filterType: "select",
        options: [
          { label: "Yes", value: "true" },
          { label: "No", value: "false" },
        ],
        defaultFilterType: "equals",
        configurable: true,
      },
    ],
    []
  );

  const getRows = useCallback(
    (params) => {
      if (!l1 || !l2) {
        // console.log("missing l1 or l2");
        params.failCallback();
        setHasData(false);
        return;
      }

      try {
        getInvoicesTagLevels(l1, l2, {
          pagination: {
            page: Math.floor(params.startRow / pagination.pageSize) + 1,
            pageSize: params.endRow - params.startRow,
          },
          filters: [
            ...convertFilterModel(params),
            // {
            //   field: "fieldName",
            //   operator: "equals",
            //   value: "invoiceMode",
            // },
          ],
          sorting: convertSortModel(params.sortModel),
        })
          .then((response) => {
            // console.log({ invoiceData: response.data });
            setPagination(response.pagination);
            setHasData(response.pagination.totalCount > 0);
            params.successCallback(
              formatData(response.data),
              response.pagination.totalCount
            );
          })
          .catch((err) => {
            params.failCallback();
            setHasData(false);
            dispatch(
              createAlert({
                message: err.message || "Error fetching invoices",
                timeout: 0,
                type: "error",
              })
            );
          });
      } catch (err) {
        params.failCallback();
        setHasData(false);
        dispatch(
          createAlert({
            message:
              err instanceof Error ? err.message : "Error fetching invoices",
            timeout: 0,
            type: "error",
          })
        );
      }
    },
    [job, pagination.pageSize, externalFilters, l1, l2]
  );

  useEffect(() => {
    if (gridApi.current) {
      // console.log("purgeInfiniteCache");
      gridApi.current.purgeInfiniteCache();
    }
  }, [l1, l2, gridApi.current]);

  const dataSource = useMemo(
    () => ({
      rowCount: undefined,
      getRows,
    }),
    [getRows]
  );

  useEffect(() => {
    if (gridApi.current) {
      gridApi.current.setDatasource(dataSource);
    }
  }, [dataSource, getRows]);

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      gridApi.current = params.api;
      gridApi.current.setDatasource(dataSource);

      // Initialize external filters after grid is ready
      dispatch(clearExternalFilters());
      dispatch(
        setExternalFiltersColumnDefs({
          externalFilterColumnDefs: externalFilterColumns,
        })
      );
    },
    [dataSource, dispatch, externalFilterColumns]
  );

  const SelectInvoiceRenderer = (props: any) => {
    if (!props.data || !props.data.id) {
      return <></>;
    }
    const handleClick = () => {
      dispatch(setSelectedInvoiceId(props.data.id));
      const url = new URL(window.location.href);
      url.searchParams.set("invoiceId", props.data.id);
      const searchParams = url.searchParams.toString();
      history.push(`/invoiceViewer?${searchParams}`);
    };

    const handleDeleteClick = () => {
      const invoiceId = props.data.id;

      const getMessage = () => (
        <div style={{ marginBottom: "20px" }}>
          <span>{`Are you sure you want to delete invoice with ID: ${invoiceId}?`}</span>
        </div>
      );

      confirmAlert({
        customUI: getCustomUi({
          onConfirm: () => {
            deleteTransactionLine(parseInt(invoiceId))
              .then(() => {
                dispatch(
                  createAlert({
                    message: `Invoice with ID ${invoiceId} deleted successfully.`,
                    timeout: 3000,
                    type: "success",
                  })
                );
                if (gridApi.current) {
                  gridApi.current.purgeInfiniteCache();
                  gridApi.current.setDatasource({
                    rowCount: undefined,
                    getRows,
                  });
                }
              })
              .catch((err) => {
                dispatch(
                  createAlert({
                    message: err.message || "Error deleting invoice.",
                    timeout: 0,
                    type: "error",
                  })
                );
              });
          },
          confirmCTA: "Delete Invoice",
          cancelCTA: "Cancel",
          getMessage,
        }),
      });
    };

    return (
      <div className="d-flex align-items-center">
        <Button variant="link" size="sm" onClick={handleClick}>
          View Invoice
        </Button>
        <Button
          variant="link"
          size="sm"
          onClick={handleDeleteClick}
          className="ml-2 text-danger"
        >
          <FontAwesomeIcon icon="trash" />
        </Button>
      </div>
    );
  };

  const columnDefs = useMemo(
    () => [
      {
        field: "invoiceId",
        headerName: "Invoice ID",
        sortable: true,
        filter: true,
      },
      {
        field: "invoiceType",
        headerName: "Type",
        sortable: true,
        filter: true,
      },
      {
        field: "docType",
        headerName: "Document Type",
        sortable: true,
        filter: true,
      },
      {
        field: "invoiceVendor",
        headerName: "Vendor",
        sortable: true,
        filter: true,
      },
      {
        field: "invoiceCustomer",
        headerName: "Customer",
        sortable: true,
        filter: true,
      },
      {
        field: "invoiceAmount",
        headerName: "Amount",
        sortable: true,
        filter: "agNumberColumnFilter",
        valueFormatter: (params: any) =>
          params.value
            ? new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(params.value)
            : "",
      },
      {
        field: "invoiceDate",
        headerName: "Date",
        sortable: true,
        filter: "agDateColumnFilter",
        filterParams: datefilterParamsYYYYMMDD,
      },
      {
        field: "approvalStatus",
        headerName: "Approval Status",
        sortable: true,
        filter: true,
        valueFormatter: (params: any) => {
          if (!params.value) return "";
          return params.value.charAt(0).toUpperCase() + params.value.slice(1);
        },
      },
      {
        field: "hasPOMatch",
        headerName: "Has PO Match",
        sortable: true,
        filter: true,
        valueFormatter: (params: any) =>
          params.value === "true" ? "Yes" : "No",
      },
      {
        field: "hasBankMatch",
        headerName: "Has Bank Match",
        sortable: true,
        filter: true,
        valueFormatter: (params: any) =>
          params.value === "true" ? "Yes" : "No",
      },
      {
        headerName: "Actions",
        cellRenderer: SelectInvoiceRenderer,
        sortable: false,
        filter: false,
        width: 180,
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      flex: 1,
      filter: true,
      sortable: true,
    }),
    []
  );

  const handleDocumentAction = () => {
    const currentParams = new URLSearchParams(window.location.search);
    history.push(
      `/extract/invoice/${
        currentParams.toString() ? "?" + currentParams.toString() : ""
      }`
    );
  };

  // Empty State Component
  const EmptyStateComponent = () => (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ minHeight: "400px" }}
    >
      <div className="text-center mb-4">
        <FontAwesomeIcon
          icon="file-invoice"
          size="4x"
          className="text-muted mb-3"
        />
        <h4 className="text-muted">No Invoices Found</h4>
        <p className="text-muted">
          Get started by adding your invoices, receipts, or bills
        </p>
      </div>

      <div>
        <Button
          variant="primary"
          size="lg"
          onClick={handleDocumentAction}
          className="d-flex flex-column align-items-center p-4"
          style={{ minWidth: "250px" }}
        >
          <FontAwesomeIcon icon="file-invoice" size="2x" className="mb-2" />
          <strong>Add Invoices & Receipts</strong>
          {/* <small className="mt-1">Upload PDF or image files</small> */}
        </Button>
      </div>
    </div>
  );

  // Show empty state if no invoices found
  if (hasData === false && Object.keys(externalFilters).length === 0) {
    return (
      <div className="p-4">
        {l1 && l2 && (
          <Row className="justify-content-end">
            <Col md="6" className="mb-3 d-flex justify-content-end">
              <Button
                variant="outline-primary"
                onClick={handleDocumentAction}
                className="mr-2"
              >
                <FontAwesomeIcon icon="plus" />
                <span className="ml-1">Add Invoices/Receipts</span>
              </Button>
              <Button
                variant="primary"
                onClick={() => dispatch(setDownloadInvoiceModalOpen(true))}
              >
                <FontAwesomeIcon icon="download" />
                <span className="ml-1">Download Invoices</span>
              </Button>
              <DownloadInvoiceModal />
            </Col>
          </Row>
        )}
        <Row>
          <Col md="12">
            <EmptyStateComponent />
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="p-4">
      {l1 && l2 && (
        <Row className="justify-content-end">
          <Col md="6" className="mb-3 d-flex justify-content-end">
            <Button
              variant="outline-primary"
              onClick={handleDocumentAction}
              className="mr-2"
            >
              <FontAwesomeIcon icon="plus" />
              <span className="ml-1">Add Invoices/Receipts</span>
            </Button>
            <Button
              variant="primary"
              onClick={() => dispatch(setDownloadInvoiceModalOpen(true))}
            >
              <FontAwesomeIcon icon="download" />
              <span className="ml-1">Download Invoices</span>
            </Button>
            <DownloadInvoiceModal />
          </Col>
        </Row>
      )}
      <Row className="mb-3">
        <Col md="12">
          <Row className="px-3">
            <span className="ml-2 mr-2">Filter by</span>
            {gridApi.current && (
              <ExternalFilter
                columnDefs={externalFilterColumns}
                gridApi={gridApi.current}
              />
            )}
          </Row>
        </Col>
      </Row>

      <div
        className="ag-theme-alpine"
        style={{ height: "500px", width: "100%" }}
      >
        <AgGridReact
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          rowModelType="infinite"
          cacheBlockSize={pagination.pageSize}
          cacheOverflowSize={2}
          maxConcurrentDatasourceRequests={1}
          infiniteInitialRowCount={1}
          maxBlocksInCache={10}
          pagination={true}
          paginationPageSize={pagination.pageSize}
          suppressCellFocus={true}
        />
      </div>
    </div>
  );
};

export default Invoices;
